/**
 * The critical-date register (S-A62) — the firm-wide risk view.
 *
 * ===========================================================================
 * WHY THIS SCREEN EXISTS AND WHY IT LOOKS LIKE THIS
 * ===========================================================================
 * A critical date is not a calendar event. Missing one has legal consequence, and
 * the register is what a principal opens in the morning.
 *
 * THREE THINGS PUT A DATE AT RISK, and each is a distinct kind of exposure:
 *
 *   MISSED       already a potential negligence exposure
 *   IMMINENT     inside the seven-day rung, where escalation reaches the firm's
 *                administrators rather than only the owner
 *   UNVERIFIED   nobody has independently checked the calculation, so the firm does
 *                not yet know whether the date is even right
 *
 * The third is the one firms tend not to think of as risk, and is the whole reason
 * the register requires independent verification. It is called out on its own here
 * rather than folded into a generic "needs attention".
 *
 * VERIFICATION IS A TWO-PERSON CONTROL. The person who set the date cannot verify it
 * — the server refuses with 409 `second_person_required` unless the firm operates as
 * a single practitioner, and where that applies the response says a single person
 * acted. A one-person action is never shown as a two-person approval (T-1).
 *
 * `due_on` is not editable, anywhere, by anyone. An in-place correction would destroy
 * the record of what the firm believed beforehand; a wrong date is SUPERSEDED, with a
 * reason, leaving both on the record.
 * ===========================================================================
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  usePaginatedList,
  type CriticalDate,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ConfirmModal,
  Select,
  Table,
  cn,
  useToast,
  type Column,
} from '@legalcrm/ui';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Can } from '@/auth/permissions';
import { formatDate, formatRelative, humanise } from '@/lib/format';
import { PageHeader } from '@/shell/PageHeader';

const SCOPE_OPTIONS = [
  { value: 'at-risk', label: 'At risk' },
  { value: 'upcoming', label: 'Next 30 days' },
  { value: 'open', label: 'All open' },
  { value: 'all', label: 'Everything' },
];

const KIND_OPTIONS = [
  { value: '', label: 'Any kind' },
  { value: 'limitation', label: 'Limitation' },
  { value: 'appeal', label: 'Appeal' },
  { value: 'filing', label: 'Filing' },
  { value: 'other', label: 'Other' },
];

/** The three exposures, named. Order matters: worst first. */
function riskOf(date: CriticalDate): 'missed' | 'imminent' | 'unverified' | null {
  if (date.missed_at) return 'missed';
  if (date.is_open && date.days_remaining <= 7) return 'imminent';
  if (date.is_open && !date.verified_at) return 'unverified';
  return null;
}

export function CriticalDatesPage() {
  const [scope, setScope] = useState('at-risk');
  const [kind, setKind] = useState('');
  const [confirming, setConfirming] = useState<CriticalDate | null>(null);

  // Three different routes, not three filters: `at-risk` and `upcoming` are their own
  // endpoints because the definition of "at risk" is the domain's, not a query the
  // frontend should be assembling.
  const path =
    scope === 'at-risk'
      ? STAFF.criticalDatesAtRisk
      : scope === 'upcoming'
        ? STAFF.criticalDatesUpcoming
        : STAFF.criticalDates;

  const list = usePaginatedList<CriticalDate>(
    qk.list('critical-dates', { scope }),
    path,
    {
      kind: kind || undefined,
      ...(scope === 'open' ? { open: 'true' } : {}),
      ...(scope === 'upcoming' ? { days: 30 } : {}),
    },
  );

  const { resetCursor } = list;
  useEffect(() => {
    resetCursor();
  }, [scope, kind, resetCursor]);

  const counts = list.items.reduce(
    (acc, date) => {
      const risk = riskOf(date);
      if (risk) acc[risk] += 1;
      return acc;
    },
    { missed: 0, imminent: 0, unverified: 0 },
  );

  const columns: Column<CriticalDate>[] = [
    {
      key: 'kind',
      header: 'Kind',
      width: 'w-32',
      render: (row) => <span className="font-medium text-ink">{humanise(row.kind)}</span>,
    },
    {
      key: 'due_on',
      header: 'Due',
      render: (row) => {
        const risk = riskOf(row);
        return (
          <div>
            {/* The absolute date leads. A limitation date rendered only as
                "in 2 days" is not a date anyone can act on. */}
            <p
              className={cn(
                'tabular-nums',
                risk === 'missed' || risk === 'imminent'
                  ? 'font-semibold text-danger'
                  : 'text-ink',
              )}
            >
              {formatDate(row.due_on)}
            </p>
            <p className="text-xs text-ink-muted">
              {row.missed_at ? 'Missed' : formatRelative(row.due_on)}
            </p>
          </div>
        );
      },
    },
    {
      key: 'case',
      header: 'Matter',
      // Every row used to read "Open matter", which made the column useless for the one
      // thing this screen exists to do: see at a glance whose deadline is at risk. The
      // reference and the client's name both come from the API now.
      render: (row) => (
        <Link
          to={`/cases/${row.case}`}
          onClick={(event) => event.stopPropagation()}
          className="block min-w-0 text-primary hover:underline"
        >
          <span className="block truncate font-medium">
            {row.case_number ?? 'Open matter'}
          </span>
          {row.client_name && (
            <span className="block truncate text-xs text-ink-muted">{row.client_name}</span>
          )}
        </Link>
      ),
    },
    {
      key: 'basis',
      header: 'Basis',
      hideOnMobile: true,
      render: (row) => (
        // The basis is what makes verification possible at all: without a stated
        // "why this date, computed how" there is nothing for a second person to check.
        <p className="max-w-md truncate text-sm text-ink-muted" title={row.basis}>
          {row.basis || <span className="italic">No basis recorded</span>}
        </p>
      ),
    },
    {
      key: 'risk',
      header: 'Status',
      render: (row) => {
        const risk = riskOf(row);
        if (row.satisfied_at) return <Badge tone="success" size="sm">Satisfied</Badge>;
        if (row.superseded_by) return <Badge tone="neutral" size="sm">Superseded</Badge>;
        if (risk === 'missed') return <Badge tone="danger" size="sm" dot>Missed</Badge>;
        if (risk === 'imminent')
          return (
            <div className="flex flex-col items-start gap-1">
              <Badge tone="danger" size="sm" dot>
                Due within 7 days
              </Badge>
              {!row.verified_at && (
                <Badge tone="warning" size="sm">
                  Unverified
                </Badge>
              )}
            </div>
          );
        if (risk === 'unverified')
          return <Badge tone="warning" size="sm" dot>Unverified</Badge>;
        return <Badge tone="success" size="sm">Verified</Badge>;
      },
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (row) =>
        row.is_open && !row.verified_at ? (
          // `critical_date.approve` — paralegals may SET a date but not verify one,
          // which is what keeps verification independent of the setter.
          <Can module="critical_date" action="approve">
            <Button
              size="sm"
              variant="secondary"
              onClick={(event) => {
                event.stopPropagation();
                setConfirming(row);
              }}
            >
              Verify
            </Button>
          </Can>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title="Critical dates"
        description="Dates the firm must not miss. Never shown to a client, on any surface."
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <RiskTile
          label="Missed"
          value={counts.missed}
          tone="danger"
          note="Already a potential exposure"
        />
        <RiskTile
          label="Due within 7 days"
          value={counts.imminent}
          tone="danger"
          note="Escalating to administrators"
        />
        <RiskTile
          label="Unverified"
          value={counts.unverified}
          tone="warning"
          note="Nobody has checked the calculation"
        />
      </div>

      <div className="mb-3 grid gap-2 sm:grid-cols-2 lg:w-1/2">
        <Select
          label="Scope"
          hideLabel
          options={SCOPE_OPTIONS}
          value={scope}
          onChange={(event) => setScope(event.target.value)}
        />
        <Select
          label="Kind"
          hideLabel
          options={KIND_OPTIONS}
          value={kind}
          onChange={(event) => setKind(event.target.value)}
        />
      </div>

      <Table
        caption="Critical date register"
        columns={columns}
        rows={list.items}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        emptyTitle={scope === 'at-risk' ? 'Nothing at risk' : 'No critical dates'}
        emptyDescription={
          scope === 'at-risk'
            ? 'No date is missed, imminent or unverified.'
            : 'Nothing matches these filters.'
        }
        pagination={{
          hasNext: list.hasNext,
          hasPrevious: list.hasPrevious,
          onNext: list.goNext,
          onPrevious: list.goPrevious,
          summary: `${list.items.length} shown`,
        }}
      />

      {confirming && (
        <VerifyDialog date={confirming} onClose={() => setConfirming(null)} />
      )}
    </>
  );
}

function RiskTile({
  label,
  value,
  tone,
  note,
}: {
  label: string;
  value: number;
  tone: 'danger' | 'warning';
  note: string;
}) {
  return (
    <Card className={value > 0 ? (tone === 'danger' ? 'border-danger/40' : 'border-warning/40') : undefined}>
      <CardBody className="flex flex-col gap-0.5">
        <p className="text-sm text-ink-muted">{label}</p>
        <p
          className={cn(
            'text-3xl font-semibold tabular-nums',
            value === 0 ? 'text-ink-muted' : tone === 'danger' ? 'text-danger' : 'text-warning',
          )}
        >
          {value}
        </p>
        <p className="text-xs text-ink-subtle">{note}</p>
      </CardBody>
    </Card>
  );
}

function VerifyDialog({ date, onClose }: { date: CriticalDate; onClose: () => void }) {
  const api = useApi();
  const { toast } = useToast();

  const verify = useApiMutation<CriticalDate & { control?: { single_practitioner?: boolean } }, void>(
    {
      mutationFn: () => api.post(STAFF.criticalDateVerify(date.id)),
      invalidates: [qk.all('critical-dates'), qk.all('cases')],
    },
    {
      onSuccess: (updated) => {
        // T-1 again: a one-person action is never represented as two.
        toast({
          title: updated.control?.single_practitioner
            ? 'Verified under single-practitioner mode'
            : 'Critical date verified',
          description: updated.control?.single_practitioner
            ? 'Recorded as a single-person action, because no other qualified verifier was available.'
            : 'The calculation has been independently confirmed.',
          tone: updated.control?.single_practitioner ? 'warning' : 'success',
        });
        onClose();
      },
      onError: (error) => {
        toast({
          title: 'Verification refused',
          // 409 `second_person_required` — "the person who set this date cannot
          // verify it". Actionable, and it names the next step. Shown verbatim.
          description:
            error instanceof ConflictError
              ? error.detail
              : 'The date could not be verified. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  return (
    <ConfirmModal
      open
      onClose={onClose}
      title="Verify this date"
      description={`${humanise(date.kind)}, due ${formatDate(date.due_on)}.`}
      confirmLabel="Confirm the calculation"
      loading={verify.isPending}
      onConfirm={() => verify.mutate()}
      consequences={
        <div className="flex flex-col gap-3">
          <div className="rounded border bg-surface-sunken px-3 py-2">
            <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
              Stated basis
            </p>
            <p className="mt-1 whitespace-pre-line text-base text-ink">
              {date.basis || 'No basis was recorded, which means there is nothing to check.'}
            </p>
          </div>
          <p className="text-sm text-ink-muted">
            You are confirming that this date is correctly calculated from the basis above.
            You cannot verify a date you set yourself — that separation is what makes the
            check worth anything.
          </p>
        </div>
      }
    />
  );
}
